<!--
  IconSelector Component
  
  A reusable dialog component for selecting icons from a list.
  
  Props:
  - value (Boolean): Controls dialog visibility (v-model)
  - currentIcon (String): Currently selected icon identifier
  - icons (Array): Array of icon objects with { icon, text } properties
  - loading (Boolean): Shows loading state
  - maxWidth (String|Number): Maximum width of the dialog (default: 600)
  - gridCols (Number): Number of grid columns (3-8, default: 6)
  
  Events:
  - input: Emitted when dialog visibility changes
  - icon-selected: Emitted when an icon is selected
  - cancelled: Emitted when selection is cancelled
  
  Usage:
  <IconSelector
    v-model="showDialog"
    :current-icon="selectedIcon"
    :icons="iconList"
    :loading="loadingIcons"
    @icon-selected="onIconSelected"
    @cancelled="onCancelled"
  />
-->
<template>
  <v-dialog v-model="dialog" :max-width="maxWidth + 'px'" @input="handleDialogClose">
    <v-card>
      <v-card-title class="m-4 card-title">
        Select an Icon
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-text-field
        v-model="searchTerm"
        placeholder="Search icons..."
        prepend-inner-icon="mdi-magnify"
        outlined
        class="card-title"
        dense
        hide-details
      ></v-text-field>

      <v-card-text>
        <div class="icon-selector">
          <div :class="`grid grid-cols-${gridCols} gap-2`">
            <div
              v-if="loading"
              :class="`col-span-${gridCols} flex flex-col items-center justify-center h-64 w-full`"
            >
              <v-progress-circular
                :color="$vuetify.theme.currentTheme.primary"
                indeterminate
                :size="50"
                :width="5"
              ></v-progress-circular>
              <div class="mt-4 text-center">
                <span>Loading icons...</span>
              </div>
            </div>
            <template v-else>
              <div
                v-for="icon in filteredIcons"
                :key="icon.icon"
                class="icon-option flex flex-col justify-center items-center text-center rounded-lg p-2"
                :class="{ selected: selectedIcon && selectedIcon.icon === icon.icon }"
                @click="selectIcon(icon)"
              >
                <v-icon :class="icon">{{ icon.icon }}</v-icon>
                <p class="text-xs mt-1">{{ icon.text }}</p>
              </div>

              <!-- Load More Button -->
              <div v-if="hasMoreIcons" :class="`col-span-${gridCols} flex justify-center py-4`">
                <v-btn color="primary" outlined small @click="loadMoreIcons">
                  Load More Icons ({{ icons.length - visibleIconsCount }} remaining)
                </v-btn>
              </div>
            </template>
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="border-t">
        <v-spacer></v-spacer>
        <v-btn text class="normal-case" @click="cancelSelection"> Cancel </v-btn>
        <v-btn
          class="normal-case"
          color="primary"
          @click="confirmSelection"
          :disabled="!selectedIcon"
        >
          <v-icon left>mdi-content-save</v-icon>
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'IconSelector',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    currentIcon: {
      type: String,
      default: ''
    },
    icons: {
      type: Array,
      default: () => [],
      validator: (icons) => {
        return icons.every(
          (icon) => (typeof icon === 'object' && icon.icon && icon.text) || typeof icon === 'string'
        )
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    maxWidth: {
      type: [String, Number],
      default: 600
    },
    gridCols: {
      type: Number,
      default: 6,
      validator: (value) => value >= 3 && value <= 8
    }
  },
  data() {
    return {
      searchTerm: '',
      selectedIcon: null,
      visibleIconsCount: 60, // Show only 60 icons initially
      iconBatchSize: 30 // Load 30 more icons each time
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    filteredIcons() {
      if (!this.searchTerm) {
        return this.icons.slice(0, this.visibleIconsCount)
      }
      const filtered = this.icons.filter(
        (icon) =>
          icon.icon.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          (icon.text && icon.text.toLowerCase().includes(this.searchTerm.toLowerCase()))
      )
      // When searching, show all results
      return this.searchTerm ? filtered : filtered.slice(0, this.visibleIconsCount)
    },
    hasMoreIcons() {
      if (this.searchTerm) return false
      return this.visibleIconsCount < this.icons.length
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initializeSelection()
      } else {
        this.resetSelection()
      }
    },
    currentIcon: {
      handler(newIcon) {
        if (newIcon && this.icons.length > 0) {
          this.setCurrentIcon(newIcon)
        }
      },
      immediate: true
    },
    icons: {
      handler(newIcons) {
        if (newIcons.length > 0 && this.currentIcon) {
          this.setCurrentIcon(this.currentIcon)
        }
      },
      immediate: true
    },
    searchTerm() {
      // Reset visible count when searching
      if (this.searchTerm) {
        this.visibleIconsCount = this.icons.length // Show all when searching
      } else {
        this.visibleIconsCount = 60 // Reset to initial count when not searching
      }
    }
  },
  methods: {
    loadMoreIcons() {
      this.visibleIconsCount = Math.min(
        this.visibleIconsCount + this.iconBatchSize,
        this.icons.length
      )
    },

    initializeSelection() {
      this.searchTerm = ''
      if (this.currentIcon) {
        this.setCurrentIcon(this.currentIcon)
      }
    },

    setCurrentIcon(iconValue) {
      const matchedIcon = this.icons.find(
        (icon) => icon.icon === iconValue || (typeof icon === 'string' && icon === iconValue)
      )

      if (matchedIcon) {
        this.selectedIcon = matchedIcon
        // Move the selected icon to the top of the list for better UX
        this.moveIconToTop(matchedIcon)
      }
    },

    moveIconToTop(targetIcon) {
      const iconIndex = this.icons.findIndex((icon) => icon.icon === targetIcon.icon)
      if (iconIndex > 0) {
        // Create a new array with the selected icon at the top
        const iconsArray = [...this.icons]
        const [movedIcon] = iconsArray.splice(iconIndex, 1)
        iconsArray.unshift(movedIcon)
        this.$emit('update:icons', iconsArray)
      }
    },

    selectIcon(icon) {
      this.selectedIcon = icon
    },

    confirmSelection() {
      if (this.selectedIcon) {
        this.$emit('icon-selected', this.selectedIcon)
        this.closeDialog()
      }
    },

    cancelSelection() {
      this.$emit('cancelled')
      this.closeDialog()
    },

    closeDialog() {
      this.dialog = false
    },

    handleDialogClose(value) {
      if (!value) {
        this.resetSelection()
      }
    },

    resetSelection() {
      this.searchTerm = ''
      this.selectedIcon = null
      this.visibleIconsCount = 60 // Reset to initial count
    }
  }
}
</script>

<style scoped>
.icon-selector {
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
}

.icon-option {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  min-height: 80px;
}

.icon-option:hover {
  background-color: rgba(59, 130, 246, 0.1);
  transform: scale(1.02);
}

.icon-option.selected {
  background-color: rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
  transform: scale(1.05);
}

.icon-option .v-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.grid {
  display: grid;
}
.card-title {
  padding: 16px !important;
}

.grid-cols-6 {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.gap-2 {
  gap: 0.5rem;
}

.col-span-6 {
  grid-column: span 6 / span 6;
}

@media (max-width: 768px) {
  .grid-cols-6 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .grid-cols-6 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
